import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, OnInit, Output, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AuthService as RealAuthService } from '../../../../../../api/services/auth.service';

// ── Panel-mode (side panel) data ───────────────────────────────────────────

export interface AiAgent {
  id: string;
  name: string;
  description: string;
  color: string;
  gradientFrom: string;
  gradientTo: string;
  icon: string;
  prompts: string[];
}

export const AGENTS: AiAgent[] = [
  {
    id: 'security-assistant',
    name: 'Security Assistant',
    description: 'General security posture guidance and analysis',
    color: '#8B5CF6',
    gradientFrom: '#8B5CF6',
    gradientTo: '#6D28D9',
    icon: 'shield',
    prompts: [
      'What is our current security posture score?',
      'Summarize the top 5 critical security gaps.',
      'What should I prioritize first to reduce risk fastest?',
      'Give me an overview of this month\'s security activity.',
      'Which areas need immediate attention?',
    ],
  },
  {
    id: 'asset-explorer',
    name: 'Asset Explorer',
    description: 'Explore and analyze your asset inventory',
    color: '#F59E0B',
    gradientFrom: '#F59E0B',
    gradientTo: '#D97706',
    icon: 'layers',
    prompts: [
      'Show the assets with the highest risk exposure.',
      'List all unscanned or unverified assets.',
      'Which assets have open vulnerabilities by severity?',
      'Show cloud assets with critical exposure.',
      'What is the asset distribution by category?',
    ],
  },
  {
    id: 'risk-manager',
    name: 'Risk Manager',
    description: 'Identify, assess, and prioritize risks',
    color: '#EF4444',
    gradientFrom: '#EF4444',
    gradientTo: '#DC2626',
    icon: 'chart',
    prompts: [
      'What are my top 5 critical risks right now?',
      'Which risks increased the most this month?',
      'Map current risks to their associated controls and tasks.',
      'Show the risk trend over the last quarter.',
      'List risks with no assigned owner or mitigation plan.',
    ],
  },
  {
    id: 'compliance-manager',
    name: 'Compliance Manager',
    description: 'Ensure alignment with security standards',
    color: '#14B8A6',
    gradientFrom: '#14B8A6',
    gradientTo: '#0D9488',
    icon: 'verify',
    prompts: [
      'What compliance frameworks are we currently tracking?',
      'Show controls that still need evidence or attestation.',
      'Which compliance gaps are highest priority to close?',
      'What is our NIST CSF coverage percentage?',
      'Generate a compliance summary for the last 30 days.',
    ],
  },
  {
    id: 'dmarc-manager',
    name: 'DMARC Manager',
    description: 'Monitor email authentication and DMARC policy',
    color: '#3B82F6',
    gradientFrom: '#3B82F6',
    gradientTo: '#2563EB',
    icon: 'email',
    prompts: [
      'What is our current DMARC policy and enforcement status?',
      'Which domains are missing SPF or DKIM records?',
      'Show email authentication compliance across all domains.',
      'Are there any DMARC failures in the last 7 days?',
      'Which domains are most at risk of email spoofing?',
    ],
  },
  {
    id: 'supply-chain',
    name: 'Supply Chain Manager',
    description: 'Track and manage third-party and vendor risks',
    color: '#A855F7',
    gradientFrom: '#A855F7',
    gradientTo: '#9333EA',
    icon: 'supply',
    prompts: [
      'List vendors with unresolved critical risks.',
      'Show third-party dependencies with highest exposure.',
      'Which suppliers have not completed their security assessment?',
      'What is our supply chain risk score?',
      'Show recent vendor risk incidents.',
    ],
  },
  {
    id: 'attack-surface',
    name: 'Attack Surface Manager',
    description: 'Monitor and reduce your external attack surface',
    color: '#F97316',
    gradientFrom: '#F97316',
    gradientTo: '#EA580C',
    icon: 'attack',
    prompts: [
      'What is our current external attack surface exposure?',
      'Show all publicly exposed assets with critical vulnerabilities.',
      'Which attack vectors have increased this month?',
      'List open ports and services that should be closed.',
      'Compare attack surface now vs 30 days ago.',
    ],
  },
];
const RECENT_CHATS = [
  { id: '2', title: 'What are my critical risks?', time: '15m ago' },
  { id: '1', title: 'Show top 5 cloud assets with exposure', time: '9:00 AM' },
  { id: '3', title: 'Tasks overdue this month', time: '1h ago' },
  { id: '4', title: 'Policy coverage for NIST AI...', time: '3h ago' },
  { id: '5', title: 'Show open action items', time: 'Yesterday' },
];

@Component({
  selector: 'app-ai-welcome-state',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './ai-welcome-state.component.html',
  styleUrl: './ai-welcome-state.component.css',
})
export class AiWelcomeStateComponent implements OnInit {
  @Input() isExpanded = false;
  @Output() suggestionSelected = new EventEmitter<{ query: string; agentId: string; agentName: string; agentColor: string; agentIcon: string }>();

  private readonly realAuth = inject(RealAuthService);
  profile: { name?: string; email?: string } | null = null;

  ngOnInit(): void {
    const token = localStorage.getItem('accessToken');
    this.realAuth.getProfile(token).subscribe({
      next: (data) => { this.profile = data.data; },
      error: () => {},
    });
  }

  // Panel-mode data
  readonly agents = AGENTS;
  readonly recentChats = RECENT_CHATS;
  activeAgent = AGENTS[0];

  draft = '';
  activeDropdown: 'input' | 'change' | null = null;

  @HostListener('document:click')
  onDocumentClick(): void {
    this.activeDropdown = null;
  }

  get userName(): string {
    const name = this.profile?.name ?? '';
    return name.split(' ')[0] || 'there';
  }

  get greeting(): string {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  }

  toggleDropdown(which: 'input' | 'change', event: MouseEvent): void {
    event.stopPropagation();
    this.activeDropdown = this.activeDropdown === which ? null : which;
  }

  selectAgent(agent: AiAgent): void {
    this.activeAgent = agent;
    this.activeDropdown = null;
  }

  submit(query?: string): void {
    const text = (query ?? this.draft).trim();
    if (!text) return;
    this.draft = '';
    this.suggestionSelected.emit({
      query: text,
      agentId: this.activeAgent.id,
      agentName: this.activeAgent.name,
      agentColor: this.activeAgent.color,
      agentIcon: this.activeAgent.icon,
    });
  }

  submitFromKeyboard(event: Event): void {
    const ke = event as KeyboardEvent;
    if (ke.shiftKey) return;
    ke.preventDefault();
    this.submit();
  }

  getAgentSvg(icon: string): string {
    const icons: Record<string, string> = {
      shield: 'M12 2l7 4v5c0 5-3.5 9.7-7 11C8.5 20.7 5 16 5 11V6l7-4z',
      layers: 'M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5',
      chart: 'M3 3v18h18M7 16l4-4 4 4 4-8',
      verify: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
      email: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
      supply: 'M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0',
      attack: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
    };
    return icons[icon] ?? icons['shield'];
  }
}
